import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Calculator, ArrowRight, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const PERSONAL = 1000;
const OVERRIDE = [200, 100, 75, 50, 50, 25];
const money = (n) => `$${Math.round(n).toLocaleString()}`;

export default function CalculatorPublic() {
  const navigate = useNavigate();
  const [width, setWidth] = useState(5);
  const [sales, setSales] = useState(1);
  const [copied, setCopied] = useState(false);

  const personalMonthly = useMemo(() => 1 * sales * PERSONAL, [sales]);
  const rows = useMemo(() => OVERRIDE.map((rate, i) => {
    const level = i + 1;
    const reps = Math.pow(width, level);
    return { level, reps, rate, monthly: reps * sales * rate };
  }), [width, sales]);

  const monthlyTotal = personalMonthly + rows.reduce((a, r) => a + r.monthly, 0);
  const teamSize = rows.reduce((a, r) => a + r.reps, 0);

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/calculator`);
    setCopied(true);
    toast.success("Calculator link copied!");
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="bg-navy-deep text-white afha-grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-patriot-light mb-3">AFHA Income Calculator</p>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">See what you could earn</h1>
          <p className="mt-5 text-lg text-white/75 max-w-2xl leading-relaxed">
            Personal sales pay <span className="text-white font-semibold">$1,000</span> each, plus overrides across
            six levels of your team. Adjust the numbers below to model your income.
          </p>
          <Button onClick={copyLink} data-testid="copy-calc-link" variant="outline" className="mt-6 rounded-full border-white/30 bg-transparent text-white hover:bg-white hover:text-navy gap-2">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy shareable link
          </Button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        <div className="bg-white border border-border rounded-xl p-6 sm:p-8" data-testid="override-calculator">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 rounded-lg bg-navy/5 flex items-center justify-center"><Calculator className="h-5 w-5 text-navy" /></div>
            <div>
              <h2 className="font-display text-2xl text-navy">Override Calculator</h2>
              <p className="text-sm text-muted-foreground">Here is an example of earnings if you build a good team.</p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-2xl">
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-navy">Reps each rep recruits</label>
                <span className="font-display text-2xl text-patriot font-bold" data-testid="calc-width-value">{width}</span>
              </div>
              <Slider min={1} max={10} step={1} value={[width]} onValueChange={(v) => setWidth(v[0])} data-testid="calc-width-slider" />
              <p className="text-xs text-muted-foreground mt-2">"Use 5, get 5" — each rep sponsors {width} reps.</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-navy">Physician sales / rep / month</label>
                <span className="font-display text-2xl text-patriot font-bold" data-testid="calc-sales-value">{sales}</span>
              </div>
              <Slider min={1} max={5} step={1} value={[sales]} onValueChange={(v) => setSales(v[0])} data-testid="calc-sales-slider" />
              <p className="text-xs text-muted-foreground mt-2">Physicians each rep onboards per month.</p>
            </div>
          </div>

          <div className="mt-8 rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-navy font-semibold">Level</TableHead>
                  <TableHead className="text-navy font-semibold text-right">Reps</TableHead>
                  <TableHead className="text-navy font-semibold text-right">Comp / sale</TableHead>
                  <TableHead className="text-navy font-semibold text-right">Monthly earnings</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="bg-patriot/5">
                  <TableCell className="font-semibold text-patriot">Personal (You)</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">1</TableCell>
                  <TableCell className="text-right text-muted-foreground">$1,000</TableCell>
                  <TableCell className="text-right font-semibold text-navy">{money(personalMonthly)}</TableCell>
                </TableRow>
                {rows.map((r) => (
                  <TableRow key={r.level}>
                    <TableCell className="font-medium text-navy">{r.level === 1 ? "Level 1 (First level)" : `Level ${r.level}`}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{r.reps.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-muted-foreground">${r.rate}</TableCell>
                    <TableCell className="text-right font-semibold text-navy">{money(r.monthly)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-muted/40 border border-border rounded-lg p-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Total team</p>
              <p className="font-display text-3xl text-navy mt-1" data-testid="calc-team-size">{teamSize.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">reps across 6 levels</p>
            </div>
            <div className="bg-muted/40 border border-border rounded-lg p-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Monthly income</p>
              <p className="font-display text-3xl text-navy mt-1" data-testid="calc-monthly">{money(monthlyTotal)}</p>
              <p className="text-xs text-muted-foreground">per month</p>
            </div>
            <div className="bg-patriot text-white rounded-lg p-5">
              <p className="text-xs uppercase tracking-wide text-white/80">Annual income</p>
              <p className="font-display text-3xl mt-1" data-testid="calc-annual">{money(monthlyTotal * 12)}</p>
              <p className="text-xs text-white/80">per year</p>
            </div>
          </div>

          <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
            This is a hypothetical illustration based on the assumptions you set above, not a
            guarantee of income. Actual results depend on your effort, your team's activity, and market conditions.
          </p>
        </div>

        <div className="mt-8 bg-navy-deep text-white rounded-xl p-8 afha-grain flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl">Ready to start earning?</h2>
            <p className="text-white/70 mt-1 text-sm">Become an AFHA representative and build your own team.</p>
          </div>
          <Button onClick={() => navigate("/signup")} data-testid="calc-join-btn" className="rounded-full bg-patriot hover:bg-patriot-dark text-white h-12 px-8 gap-2 shrink-0">
            Become a Rep <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
